import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Folder, Image as ImageIcon, Loader2, X, ChevronRight, ArrowLeft, Search, AlertCircle, Database, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Public picker for selecting one (or many) images from the Bunny.net
// Storage Zone.
//
//   • Single-select (default):
//       <BunnyStoragePicker onSelect={(file) => …} onClose={…} />
//     `onSelect` fires immediately when a file is clicked.
//
//   • Multi-select:
//       <BunnyStoragePicker multiple title="Add images" onSelect={(files) => …} onClose={…} />
//     The user toggles files with checkbox overlays; `onSelect` fires
//     ONCE with the full array when they hit Confirm.
//
// Auth is carried by the shared httpOnly admin session cookie set via
// AdminAuthGate — the picker only needs `credentials: "include"` on its
// fetches to travel with that cookie.
const BunnyStoragePicker = ({ onClose, onSelect, multiple = false, title = "" }) => {
  const [prefix, setPrefix] = useState("");          // current folder path inside the zone
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [imagesOnly, setImagesOnly] = useState(true);
  const [picking, setPicking] = useState(null);     // path being applied (single-mode)
  // Multi-mode selection. Map<path, file> — preserves selection order via
  // Map iteration and survives folder navigation, so editors can pull
  // images from several subfolders before confirming.
  const [selected, setSelected] = useState(() => new Map());
  const [confirming, setConfirming] = useState(false);

  const fetchList = useCallback(async (nextPrefix) => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({
        prefix: nextPrefix || "",
        images_only: String(imagesOnly),
      });
      const res = await fetch(`${API}/api/admin/bunny-storage/list?${qs.toString()}`, {
        credentials: "include",
        cache: "no-store",
      });
      if (res.status === 401) throw new Error("Admin session expired — please reload.");
      if (res.status === 503) throw new Error("Bunny Storage is not configured on the server.");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(Array.isArray(data.objects) ? data.objects : []);
    } catch (err) {
      setError(err.message || "Failed to load Bunny Storage listing");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [imagesOnly]);

  useEffect(() => { fetchList(prefix); }, [fetchList, prefix]);

  // ESC closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Breadcrumb segments derived from the current prefix.
  const segments = useMemo(() => {
    const parts = (prefix || "").replace(/\/$/, "").split("/").filter(Boolean);
    const acc = [];
    let cum = "";
    parts.forEach((p) => {
      cum = cum ? `${cum}/${p}` : p;
      acc.push({ label: p, path: `${cum}/` });
    });
    return acc;
  }, [prefix]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.name.toLowerCase().includes(q));
  }, [items, query]);

  const handleOpenFolder = (item) => {
    setQuery("");
    setPrefix(item.path.endsWith("/") ? item.path : `${item.path}/`);
  };

  const handleSelectFile = async (item) => {
    if (!item.public_url) return;
    if (multiple) {
      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(item.path)) next.delete(item.path);
        else next.set(item.path, item);
        return next;
      });
      return;
    }
    setPicking(item.path);
    try {
      await Promise.resolve(onSelect?.(item));
    } finally {
      setPicking(null);
    }
  };

  const handleConfirmMulti = async () => {
    if (!multiple || selected.size === 0) return;
    setConfirming(true);
    try {
      await Promise.resolve(onSelect?.(Array.from(selected.values())));
    } finally {
      setConfirming(false);
    }
  };

  const handleClearSelection = () => setSelected(new Map());

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="image-edit-backdrop"
      role="dialog"
      aria-modal="true"
      data-edit-allow="true"
      onClick={handleBackdrop}
      data-testid="bunny-storage-picker"
    >
      <div
        className="image-edit-modal image-edit-modal--fullscreen"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="image-edit-header">
          <div>
            <span className="image-edit-eyebrow">
              <Database className="w-3 h-3 inline-block mr-1.5 align-[-2px]" strokeWidth={1.8} />
              {title || "Bunny Storage"}
            </span>
            <span className="image-edit-slot">
              {prefix ? `/${prefix.replace(/\/$/, "")}` : "/ (root)"}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="image-edit-close"
            data-testid="bunny-storage-close"
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </header>

        {/* Breadcrumb + filters */}
        <section className="image-edit-section" style={{ paddingTop: 6 }}>
          <div className="flex flex-wrap items-center gap-1 text-xs text-[#5C5248]" data-testid="bunny-storage-breadcrumb">
            <button
              type="button"
              onClick={() => { setPrefix(""); setQuery(""); }}
              className="px-2 py-1 hover:text-[#C16542] disabled:opacity-50"
              disabled={!prefix}
              data-testid="bunny-storage-crumb-root"
            >
              <ArrowLeft className="w-3 h-3 inline-block mr-1 align-[-2px]" /> Root
            </button>
            {segments.map((seg, i) => (
              <React.Fragment key={seg.path}>
                <ChevronRight className="w-3 h-3 text-[#5C5248]/60" />
                <button
                  type="button"
                  onClick={() => { setPrefix(seg.path); setQuery(""); }}
                  className={`px-2 py-1 hover:text-[#C16542] truncate max-w-[180px] ${i === segments.length - 1 ? "text-[#2C2621] font-medium" : ""}`}
                  title={seg.label}
                  data-testid={`bunny-storage-crumb-${i}`}
                >
                  {seg.label}
                </button>
              </React.Fragment>
            ))}
          </div>

          <div className="image-edit-row mt-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5248]" strokeWidth={1.6} />
              <input
                type="search"
                placeholder="Filter the current folder…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="image-edit-input"
                style={{ paddingLeft: 30 }}
                data-testid="bunny-storage-search"
              />
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs text-[#5C5248] whitespace-nowrap pl-2 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={imagesOnly}
                onChange={(e) => setImagesOnly(e.target.checked)}
                data-testid="bunny-storage-images-only"
              />
              Images only
            </label>
          </div>
        </section>

        {/* Grid */}
        <section className="image-edit-section" data-testid="bunny-storage-grid-section">
          {loading && (
            <p className="image-edit-hint" data-testid="bunny-storage-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin inline-block align-[-2px] mr-1" />
              Loading Bunny Storage…
            </p>
          )}
          {!loading && error && (
            <p className="image-edit-hint" data-testid="bunny-storage-error" style={{ color: "#C16542" }}>
              <AlertCircle className="w-3.5 h-3.5 inline-block align-[-2px] mr-1" />
              {error}
            </p>
          )}
          {!loading && !error && filtered.length === 0 && (
            <p className="image-edit-hint" data-testid="bunny-storage-empty">
              {items.length === 0
                ? "This folder is empty (or contains no images)."
                : `Nothing matches “${query}”.`}
            </p>
          )}
          {!loading && !error && filtered.length > 0 && (
            <div className="image-edit-pexels-grid" data-testid="bunny-storage-grid">
              {filtered.map((it) => {
                const key = `bunny-${it.path}-${it.guid || it.name}`;
                if (it.is_directory) {
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleOpenFolder(it)}
                      className="image-edit-pexels-card"
                      style={{ cursor: "pointer", background: "#F2EBE1" }}
                      data-testid={`bunny-storage-folder-${it.name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}
                      title={`Open folder ${it.name}`}
                    >
                      <span className="image-edit-pexels-thumb" style={{ background: "#E8DBC4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Folder className="w-8 h-8 text-[#5C5248]" strokeWidth={1.4} />
                      </span>
                      <div className="image-edit-pexels-meta">
                        <span className="image-edit-pexels-credit" style={{ pointerEvents: "none" }}>
                          {it.name}
                        </span>
                      </div>
                    </button>
                  );
                }
                const isPicking = picking === it.path;
                const isSelected = multiple && selected.has(it.path);
                return (
                  <div key={key} className="image-edit-pexels-card" data-testid={`bunny-storage-file-${it.name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}>
                    <button
                      type="button"
                      onClick={() => handleSelectFile(it)}
                      className="image-edit-pexels-thumb"
                      disabled={isPicking || !it.public_url}
                      aria-label={`${multiple ? "Toggle selection of" : "Use"} ${it.name}`}
                      aria-pressed={multiple ? isSelected : undefined}
                      title={it.public_url ? (multiple ? `Toggle ${it.name}` : `Use ${it.name}`) : "CDN not configured for this file"}
                      style={isSelected ? { boxShadow: "inset 0 0 0 3px #C16542" } : undefined}
                      data-testid={`bunny-storage-file-btn-${it.name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}
                    >
                      {it.public_url ? (
                        <img
                          src={`${it.public_url}?width=320&format=webp&quality=70`}
                          alt={it.name}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.currentTarget.src = it.public_url; }}
                          style={{ backgroundColor: "#e8dfcf" }}
                        />
                      ) : (
                        <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                          <ImageIcon className="w-8 h-8 text-[#5C5248]" strokeWidth={1.4} />
                        </span>
                      )}
                      {isPicking && (
                        <span className="image-edit-pexels-applying">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </span>
                      )}
                      {multiple && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute", top: 6, right: 6,
                            width: 22, height: 22, borderRadius: 4,
                            border: "2px solid #FDFBF7",
                            background: isSelected ? "#C16542" : "rgba(44,38,33,0.55)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#FDFBF7",
                          }}
                        >
                          {isSelected && <Check className="w-3 h-3" strokeWidth={3} />}
                        </span>
                      )}
                    </button>
                    <div className="image-edit-pexels-meta">
                      <span className="image-edit-pexels-credit" title={it.name} style={{ pointerEvents: "none" }}>
                        {it.name}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Multi-mode footer: confirm / clear */}
        {multiple && (
          <section
            className="image-edit-section"
            style={{ borderTop: "1px solid rgba(44,38,33,0.10)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
            data-testid="bunny-storage-multi-footer"
          >
            <span className="text-[11px] tracking-[0.22em] uppercase text-[#5C5248]" data-testid="bunny-storage-selected-count">
              {selected.size === 0
                ? "No images selected"
                : `${selected.size} selected`}
            </span>
            {selected.size > 0 && (
              <button
                type="button"
                onClick={handleClearSelection}
                className="text-[11px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542] underline-offset-2 hover:underline"
                data-testid="bunny-storage-clear-selection"
              >
                Clear
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={onClose}
              className="image-edit-secondary"
              data-testid="bunny-storage-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmMulti}
              disabled={selected.size === 0 || confirming}
              className="image-edit-primary"
              data-testid="bunny-storage-confirm"
            >
              {confirming
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding…</>
                : <><Check className="w-3.5 h-3.5" /> Use {selected.size || ""} image{selected.size === 1 ? "" : "s"}</>}
            </button>
          </section>
        )}
      </div>
    </div>
  );
};

export default BunnyStoragePicker;
